import { FormatHelpers } from "../../helpers/format-helpers";
import { ObjectHelpers } from "../../helpers/object-helpers";
import { TraceLoggingHelpers } from "../../helpers/trace-logging-helpers";
import { RequestService } from "../../services/request.service";
import { ChartType } from "../enum/chart-type.enum";
import { ChartMode, ChartTheme } from "../window-wrapper/marketing-webchart-window-wrapper.model";
import { ChartStyleColorModel } from "./chart-style-color.model";
import { ChartStylePIPSettingsModel } from "./chart-style-pip-settings.model";
import { ChartStyleSettingsModel } from "./chart-style-settings.model";
const color = require('../../library/darken-color.js');
declare const window: any;

export class WebchartThemeManager {

    private chartThemes: Record<string, ChartStyleSettingsModel> = {};

    constructor() {

    }

    private static instance: WebchartThemeManager | null = null;
    public static getInstance() {
        if (!this.instance) {
            this.instance = new WebchartThemeManager();
        }

        return this.instance;
    }

    public loadThemeXML(basePath: string, callback?: () => void) {
        if (!window.loadedThemeXML) {
            window.loadedThemeXML = true;
            const requet = new RequestService();
            requet.get(basePath + 'assetfiles/themes/webchart-themes.xml').then((res: any) => {
                this.parseXMLToWebChartThemeModel(res);
                this.notifyHolidayWatchers();
                if (callback) {
                    callback();
                }
            }).catch(() => {
                if (callback) {
                    callback();
                }
            })
        } else {
            this.addloadThemeXMLWatchers(callback);
        }
    }

    public getChartThemeByName(name: string, isPnP: boolean, chartType: ChartType, showPostMarket: boolean, showPreMarket: boolean, pnpEnabled: boolean, chartMode: ChartMode): ChartStyleSettingsModel {
        const key = name?.toLowerCase();
        let rawThemeModel = this.chartThemes[key];

        if (!rawThemeModel) {
            rawThemeModel = this.chartThemes[ChartTheme.Light];
        } 

        let themeModel= new ChartStyleSettingsModel(ObjectHelpers.copy(rawThemeModel));
        themeModel.pnpEnabled = pnpEnabled;
        if (isPnP) {
            themeModel = this.getPIPChartStylesSettings(themeModel);
        } else {
            themeModel.chartType = chartType;
            themeModel.showPostMarket = showPostMarket;
            themeModel.showPreMarket = showPreMarket;
            themeModel.zoomLevel = 1;
        }

        return themeModel;
    }

    private notifyHolidayWatchers() {
        if (window.loadThemeXMLWatchers && window.loadThemeXMLWatchers.length) {
            for (let i = 0; i < window.loadThemeXMLWatchers.length; i++) {
                window.loadThemeXMLWatchers[i](this.chartThemes);
            }
        }
    }
    
    private addloadThemeXMLWatchers(callback?: () => void) {
        if (!window.loadThemeXMLWatchers) {
            window.loadThemeXMLWatchers = [];
        }

        window.loadThemeXMLWatchers.push((chartThemes: Record<string, ChartStyleSettingsModel>) => {
            this.chartThemes = chartThemes;
            if (callback) {
                callback();
            }
        });
    }

    private parseXMLToWebChartThemeModel(xmlString: string) {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlString, "text/xml");

        const tiThemesElements = xmlDoc.getElementsByTagName("TITHEME");
        for (let i = 0; i < tiThemesElements.length; i++) {
            try {
                const tiTheme = tiThemesElements[i];
                const name = tiTheme.getAttribute('NAME') ?? '';
                const key = tiTheme.getAttribute('NAME')?.toLowerCase();
                const jsonModel = JSON.parse(tiTheme.innerHTML);
                if (jsonModel) {
                    const mainChartStyleLayout = jsonModel['MainChartStyleLayout'];
                    const pipChartStyleLayout = jsonModel['PipChartStyleLayout'];

                    if (mainChartStyleLayout) {
                        const backgroundColorString = mainChartStyleLayout['SelectedChartBackgroundColor'];
                        const backgroundColor = this.getColorFromJSONString(backgroundColorString);
                        let chartStyleSettings: ChartStyleSettingsModel = null;
                        if (!backgroundColor.isDark()) {
                            chartStyleSettings = this.getLightThemeBaseChartStyleSettingsModel();
                        } else {
                            chartStyleSettings = this.getDarkThemeBaseChartStyleSettingsModel();
                        }

                        chartStyleSettings.name = name;

                        chartStyleSettings.selectedChartBackgroundColor = backgroundColor;
                        chartStyleSettings.selectedChartGradientColor = this.getColorFromJSONString(mainChartStyleLayout['SelectedChartGradientColor']);
                        
                        chartStyleSettings.selectedDateTextColor = this.getColorFromJSONString(mainChartStyleLayout['SelectedDateTextColor']);
                        chartStyleSettings.selectedPriceTextColor = this.getColorFromJSONString(mainChartStyleLayout['SelectedPriceTextColor']);
                        chartStyleSettings.selectedPriceTextColor = this.getColorFromJSONString(mainChartStyleLayout['SelectedPriceTextColor']);
                        chartStyleSettings.selectedUpCandleColor = this.getColorFromJSONString(mainChartStyleLayout['SelectedUpCandleColor']);
                        chartStyleSettings.selectedDownCandleColor = this.getColorFromJSONString(mainChartStyleLayout['SelectedDownCandleColor']);
                        chartStyleSettings.selectedOutlineWickColor = this.getColorFromJSONString(mainChartStyleLayout['SelectedOutlineWickColor']);
                        chartStyleSettings.selectedStatusLineTextColor = this.getColorFromJSONString(mainChartStyleLayout['SelectedStatusLineTextColor']);
                        
                        chartStyleSettings.watermarkColor = this.getWatermarkColor(mainChartStyleLayout['IsWatermarkDarkTheme']);

                        chartStyleSettings.isGradientChecked = mainChartStyleLayout['ColorPickerIsGradientChecked'];
                        chartStyleSettings.isGradientAreaColorChecked = mainChartStyleLayout['ColorPickerIsGradientAreaColorChecked'];
                        chartStyleSettings.watermarkOpacity = mainChartStyleLayout['WatermarkOpacity'];

                        if (mainChartStyleLayout['SingleStockInfoBackgroundColor']) {
                            chartStyleSettings.singleStockInfoBackgroundColor = this.getColorFromJSONString(mainChartStyleLayout['SingleStockInfoBackgroundColor']);
                        }

                        if (mainChartStyleLayout['SelectedAreaChartUpColor']) {
                            chartStyleSettings.selectedAreaChartUpColor = this.getColorFromJSONString(mainChartStyleLayout['SelectedAreaChartUpColor']);
                        }

                        if (mainChartStyleLayout['SelectedAreaChartDownColor']) {
                            chartStyleSettings.selectedAreaChartDownColor = this.getColorFromJSONString(mainChartStyleLayout['SelectedAreaChartDownColor']);
                        }

                        if (pipChartStyleLayout) {
                            chartStyleSettings.chartStylePIPSettings = new ChartStylePIPSettingsModel();
                            chartStyleSettings.chartStylePIPSettings.selectedChartBackgroundColor = this.getColorFromJSONString(pipChartStyleLayout['SelectedChartBackgroundColor']);
                            chartStyleSettings.chartStylePIPSettings.selectedChartGradientColor = this.getColorFromJSONString(pipChartStyleLayout['SelectedChartGradientColor']);
                            chartStyleSettings.chartStylePIPSettings.selectedDateTextColor = this.getColorFromJSONString(pipChartStyleLayout['SelectedDateTextColor']);
                            chartStyleSettings.chartStylePIPSettings.selectedPriceTextColor = this.getColorFromJSONString(pipChartStyleLayout['SelectedPriceTextColor']);
                            chartStyleSettings.chartStylePIPSettings.selectedUpCandleColor = this.getColorFromJSONString(pipChartStyleLayout['SelectedUpCandleColor']);
                            chartStyleSettings.chartStylePIPSettings.selectedDownCandleColor = this.getColorFromJSONString(pipChartStyleLayout['SelectedDownCandleColor']);
                            chartStyleSettings.chartStylePIPSettings.selectedOutlineWickColor = this.getColorFromJSONString(pipChartStyleLayout['SelectedOutlineWickColor']);
                            chartStyleSettings.chartStylePIPSettings.selectedStatusLineTextColor = this.getColorFromJSONString(pipChartStyleLayout['SelectedStatusLineTextColor']);
                            chartStyleSettings.chartStylePIPSettings.selectedLineColor = this.getColorFromJSONString(pipChartStyleLayout['SelectedLineColor']);
                            
                            chartStyleSettings.chartStylePIPSettings.watermarkColor = this.getWatermarkColor(pipChartStyleLayout['IsWatermarkDarkTheme']);
                            
                            chartStyleSettings.chartStylePIPSettings.isGradientChecked = pipChartStyleLayout['ColorPickerIsGradientChecked'];
                            chartStyleSettings.chartStylePIPSettings.isGradientAreaColorChecked = pipChartStyleLayout['ColorPickerIsGradientAreaColorChecked'];
                            chartStyleSettings.chartStylePIPSettings.watermarkOpacity = pipChartStyleLayout['WatermarkOpacity'];

                            if (pipChartStyleLayout['TimeframeColor']) {
                                chartStyleSettings.chartStylePIPSettings.timeframeColor = this.getColorFromJSONString(pipChartStyleLayout['TimeframeColor']);
                            }
                            
                            if (pipChartStyleLayout['SelectedAreaChartUpColor']) {
                                chartStyleSettings.chartStylePIPSettings.selectedAreaChartUpColor = this.getColorFromJSONString(pipChartStyleLayout['SelectedAreaChartUpColor']);
                            }

                            if (pipChartStyleLayout['SelectedAreaChartDownColor']) {
                                chartStyleSettings.chartStylePIPSettings.selectedAreaChartDownColor = this.getColorFromJSONString(pipChartStyleLayout['SelectedAreaChartDownColor']);
                            }
                        }

                        this.chartThemes[key] = chartStyleSettings;
                    }

                }
            } catch {}
        }

        TraceLoggingHelpers.log('loadThemeXML this.chartThemes', this.chartThemes)
    }
    
    private getWatermarkColor(isDark: boolean): ChartStyleColorModel {
        if (isDark) {
            return new ChartStyleColorModel({r: 31, g: 31, b: 31});
        }

        return new ChartStyleColorModel({r: 230, g: 230, b: 230});
    }

    private getColorFromJSONString(colorString: string) {
        let chartStyleColor: ChartStyleColorModel = null;
        if (!colorString) {
            return chartStyleColor;
        }

        const colorSplits = colorString?.split(',') ?? [];
        if (colorSplits.length == 3) {
            chartStyleColor = new ChartStyleColorModel({r: parseInt(colorSplits[0], 10), g: parseInt(colorSplits[1], 10), b: parseInt(colorSplits[2], 10)});
        } else if (colorString[0] === '#') {
            chartStyleColor = new ChartStyleColorModel(color.HEX2RGB(colorString));
        } else {
            const colorFromName = color.NAME2RGB(colorString.toLowerCase());
            chartStyleColor = new ChartStyleColorModel(colorFromName);
        }

        return chartStyleColor;
    }

    private getPIPChartStylesSettings(style: ChartStyleSettingsModel): ChartStyleSettingsModel {
        const themeModel = new ChartStyleSettingsModel(ObjectHelpers.copy(style));

        themeModel.chartType = ChartType.Area;
        themeModel.isOneColor = true;
        themeModel.selectedLineColor = new ChartStyleColorModel({ r: 0, g: 91, b: 129 });
        themeModel.chartTypeLineWidth = 1;
        themeModel.zoomLevel = -3;

        if (style.chartStylePIPSettings) {
            themeModel.isGradientAreaColorChecked = style.chartStylePIPSettings.isGradientAreaColorChecked;
            themeModel.isGradientChecked = style.chartStylePIPSettings.isGradientChecked;
            themeModel.watermarkOpacity = style.chartStylePIPSettings.watermarkOpacity;

            const selectedChartBackgroundColorRGB = style.chartStylePIPSettings.selectedChartBackgroundColor;
            themeModel.selectedChartBackgroundColor = new ChartStyleColorModel(selectedChartBackgroundColorRGB);

            const selectedChartGradientColorRGB = style.chartStylePIPSettings.selectedChartGradientColor;
            themeModel.selectedChartGradientColor = new ChartStyleColorModel(selectedChartGradientColorRGB);

            const selectedPriceTextColorRGB = style.chartStylePIPSettings.selectedPriceTextColor;
            themeModel.selectedPriceTextColor = new ChartStyleColorModel(selectedPriceTextColorRGB);

            const selectedChartDateTextColorRGB = style.chartStylePIPSettings.selectedDateTextColor;
            themeModel.selectedDateTextColor = new ChartStyleColorModel(selectedChartDateTextColorRGB);

            const selectedUpCandleColorRGB = style.chartStylePIPSettings.selectedUpCandleColor;
            themeModel.selectedUpCandleColor = new ChartStyleColorModel(selectedUpCandleColorRGB);

            const selectedDownCandleColorRGB = style.chartStylePIPSettings.selectedDownCandleColor;
            themeModel.selectedUpCandleColor = new ChartStyleColorModel(selectedDownCandleColorRGB);

            const selectedOutlineWickColorRGB = style.chartStylePIPSettings.selectedOutlineWickColor;
            themeModel.selectedUpCandleColor = new ChartStyleColorModel(selectedOutlineWickColorRGB);

            const selectedStatusLineTextColorRGB = style.chartStylePIPSettings.selectedStatusLineTextColor;
            themeModel.selectedUpCandleColor = new ChartStyleColorModel(selectedStatusLineTextColorRGB);

            const selectedLineColorRGB = style.chartStylePIPSettings.selectedLineColor;
            themeModel.selectedLineColor = new ChartStyleColorModel(selectedLineColorRGB);

            const watermarkColorRGB = style.chartStylePIPSettings.watermarkColor;
            themeModel.watermarkColor = new ChartStyleColorModel(watermarkColorRGB);

            if (style.chartStylePIPSettings.timeframeColor) {
                themeModel.timeframeColor = new ChartStyleColorModel(style.chartStylePIPSettings.timeframeColor);
            }

            if (style.chartStylePIPSettings.selectedAreaChartUpColor) {
                themeModel.selectedAreaChartUpColor = new ChartStyleColorModel(style.chartStylePIPSettings.selectedAreaChartUpColor);
            }

            if (style.chartStylePIPSettings.selectedAreaChartDownColor) {
                themeModel.selectedAreaChartDownColor = new ChartStyleColorModel(style.chartStylePIPSettings.selectedAreaChartDownColor);
            }

        } else {
            themeModel.isGradientAreaColorChecked = true;
            const isDark = style.selectedChartBackgroundColor.isDark();
            let selectedChartBackgroundColorRGB = { r: 0, g: 0, b: 0 };
            if (isDark) {
                selectedChartBackgroundColorRGB = color.HEX2RGB(style.selectedChartBackgroundColor.lighten(3));
            } else {
                selectedChartBackgroundColorRGB = color.HEX2RGB(style.selectedChartBackgroundColor.darken(5));
            }

            themeModel.selectedChartBackgroundColor = new ChartStyleColorModel(selectedChartBackgroundColorRGB);

            themeModel.isDarkTheme = themeModel.selectedChartBackgroundColor.isDark();

            let selectedChartGradientColorRGB = { r: 0, g: 0, b: 0 };
            if (isDark) {
                selectedChartGradientColorRGB = color.HEX2RGB(style.selectedChartGradientColor.lighten(5));
            } else {
                selectedChartGradientColorRGB = color.HEX2RGB(style.selectedChartGradientColor.darken(8));
            }

            themeModel.selectedChartGradientColor = new ChartStyleColorModel(selectedChartGradientColorRGB);
        }

        return themeModel;
    }

    private getLightThemeBaseChartStyleSettingsModel(): ChartStyleSettingsModel {
        const separators = FormatHelpers.getNumberSeparators();
        const themeModel = new ChartStyleSettingsModel({
            // chartType: this.chartType,
            chartTypeLineWidth: 1,
            crossHairsEnabled: false,
            curvedLineDisabled: false,
            dateSeparator: '-',
            decimalSeparator: separators.decimal,
            thousandSeparator: separators.thousand,
            earningRectangleColor: { r: 255, g: 255, b: 0 },
            getPrePostLastPriceDisabled: false,
            gridLinesEnabled: true,
            highChartIndicatorsEnabled: false,
            isComparisonSymbolPercentageMode: false,
            isDarkTheme: false,
            isGradientAreaColorChecked: true,
            isGradientChecked: true,
            isOneColor: false,
            monthDayPattern: 'MMMM d',
            postMarketColor: { r: 0, g: 0, b: 255 },
            preMarketColor: { r: 252, g: 130, b: 45 },
            selectedChartBackgroundColor: { r: 255, g: 255, b: 255 },
            selectedChartGradientColor: { r: 255, g: 255, b: 255 },
            selectedDateTextColor: { r: 0, g: 0, b: 0 },
            selectedDownCandleColor: { r: 255, g: 0, b: 0 },
            selectedLineColor: { r: 211, g: 211, b: 211 },
            selectedOutlineWickColor: { r: 128, g: 128, b: 128 },
            selectedPriceTextColor: { r: 0, g: 0, b: 0 },
            selectedStatusLineTextColor: { r: 51, g: 51, b: 51 },
            selectedUpCandleColor: { r: 0, g: 255, b: 0 },
            // showPostMarket: this.showPostMarket,
            // showPreMarket: this.showPreMarket,
            showSmartRiskLevel: false,
            showWatermark: true,
            // pnpEnabled: this.pnpEnabled,
            startDrawingToolsExpanded: false,
            watermarkColor: { r: 31, g: 31, b: 31 },
            watermarkOpacity: 34,
            // zoomLevel: 1,
        });
        return themeModel;
    }

    private getDarkThemeBaseChartStyleSettingsModel(): ChartStyleSettingsModel {
        const separators = FormatHelpers.getNumberSeparators();
        const themeModel = new ChartStyleSettingsModel({
            // chartType: this.chartType,
            chartTypeLineWidth: 1,
            selectedLineColor: { r: 255, g: 255, b: 255 },
            selectedUpCandleColor: { r: 0, g: 255, b: 0 },
            selectedDownCandleColor: { r: 255, g: 0, b: 0 },
            selectedOutlineWickColor: {
                r: 115,
                g: 115,
                b: 117
            },
            selectedDateTextColor: {
                r: 128,
                g: 128,
                b: 128
            },
            selectedPriceTextColor: {
                r: 128,
                g: 128,
                b: 128
            },
            selectedStatusLineTextColor: {
                r: 120,
                g: 120,
                b: 120
            },
            selectedChartGradientColor: {
                r: 23,
                g: 23,
                b: 23
            },
            selectedChartBackgroundColor: {
                r: 23,
                g: 23,
                b: 23
            },
            preMarketColor: {
                r: 255,
                g: 121,
                b: 0
            },
            postMarketColor: {
                r: 162,
                g: 0,
                b: 255
            },
            watermarkColor: {
                r: 230,
                g: 230,
                b: 230
            },
            earningRectangleColor: {
                r: 255,
                g: 255,
                b: 0
            },
            watermarkOpacity: 34,
            monthDayPattern: 'd MMMM',
            dateSeparator: '-',
            decimalSeparator: separators.decimal,
            thousandSeparator: separators.thousand,
            isGradientChecked: true,
            isGradientAreaColorChecked: true,
            isOneColor: false,
            isDarkTheme: true,
            showPreMarket: true,
            showPostMarket: false,
            showSmartRiskLevel: false,
            crossHairsEnabled: false,
            gridLinesEnabled: true,
            showWatermark: true,
            // pnpEnabled: this.pnpEnabled,
            isComparisonSymbolPercentageMode: false,
            curvedLineDisabled: false,
            getPrePostLastPriceDisabled: false,
            startDrawingToolsExpanded: false,
            highChartIndicatorsEnabled: false,
            // zoomLevel: 1,
        });

        return themeModel;
    }
}